import React, { useState } from 'react';
import { IconButton, Tooltip, Box } from '@mui/material';
import { Copy, Check } from 'lucide-react';
import theme from '../util/theme/theme';

interface CopyToClipboardProps {
  text: string;
  variant?: 'icon' | 'button';
  tooltipTitle?: string;
  copiedTooltipTitle?: string;
  onCopy?: () => void;
  disabled?: boolean;
}

const CopyToClipboard: React.FC<CopyToClipboardProps> = ({
  text,
  variant = 'icon',
  tooltipTitle = 'Copy to clipboard',
  copiedTooltipTitle = 'Copied!',
  onCopy,
  disabled = false,
}) => {
    const [isCopied, setIsCopied] = useState(false);

    const handleCopy = async () => {
        try {
        await navigator.clipboard.writeText(text);
        setIsCopied(true);
        onCopy?.();

        // Reset the "copied" state after 2 seconds
        setTimeout(() => setIsCopied(false), 2000);
        } catch (err) {
        console.error('Failed to copy text:', err);
        }
    };

    const currentTooltip = isCopied ? copiedTooltipTitle : tooltipTitle;
    const icon = isCopied ? <Check size={18} /> : <Copy size={16} />;

    if (variant === 'button') {
        return (
        <Tooltip title={currentTooltip}>
            <span>
            <IconButton
                onClick={handleCopy}
                disabled={disabled}
                size="small"
                sx={{
                    color: isCopied ? theme.palette.primary.main : 'inherit',
                    transition: 'color 0.2s ease',
                }}
            >
                {icon}
            </IconButton>
            </span>
        </Tooltip>
        );
    }

    return (
        <Tooltip title={currentTooltip}>
            <Box
                component="button"
                onClick={handleCopy}
                disabled={disabled}
                sx={{
                    background: 'none',
                    border: 'none',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: isCopied ? theme.palette.primary.main : 'inherit',
                    transition: 'color 0.2s ease',
                    opacity: disabled ? 0.5 : 1,
                }}>
                {icon}
            </Box>
        </Tooltip>
    );
};

export default CopyToClipboard;
